import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { COMPANY } from "@/lib/data";

export default function LegalModal({ open, onClose }) {
  useEffect(() => {
    if (open) document.body.classList.add("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-[#0F172A]/70 backdrop-blur-sm p-0 lg:p-8 flex items-end lg:items-center justify-center"
          onClick={onClose}
          data-testid="legal-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FDFBF7] w-full lg:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl lg:rounded-3xl border border-[#e2d9ce]"
          >
            <div className="sticky top-0 z-10 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#e2d9ce] px-6 lg:px-10 py-4 flex items-center justify-between">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                HMCD · Informations légales
              </div>
              <button
                data-testid="legal-close"
                onClick={onClose}
                className="h-9 w-9 rounded-full bg-[#F5F1EA] hover:bg-[#0F172A] hover:text-[#FDFBF7] transition-colors flex items-center justify-center"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 lg:px-10 py-10 lg:py-14">
              <div className="eyebrow mb-3">Mentions légales</div>
              <h2 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A]">
                Mentions légales
              </h2>

              <section className="mt-10">
                <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                  Éditeur du site
                </h3>
                <p className="text-[15px] leading-relaxed text-[#334155]">
                  <strong>HM Conseil Digital</strong>, société au capital social
                  de <strong>126 000 €</strong>, dont le siège social est situé
                  au <strong>99 quai du docteur Dervaux, 92600
                  Asnières-sur-Seine, France</strong>, immatriculée sous le
                  numéro <strong>909 453 268</strong>, représentée par son
                  Gérant, agissant et ayant les pouvoirs nécessaires.
                </p>
              </section>

              <section className="mt-8">
                <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                  Contact
                </h3>
                <ul className="space-y-1.5 text-[15px] text-[#334155]">
                  <li>
                    <span className="text-[#64748B]">Téléphone :</span>{" "}
                    <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-[#2D7A4D]">
                      {COMPANY.phone}
                    </a>
                  </li>
                  <li>
                    <span className="text-[#64748B]">Email :</span>{" "}
                    <a href={`mailto:${COMPANY.email}`} className="hover:text-[#2D7A4D]">
                      {COMPANY.email}
                    </a>
                  </li>
                  <li>
                    <span className="text-[#64748B]">Adresse :</span>{" "}
                    {COMPANY.address}, {COMPANY.city}
                  </li>
                </ul>
              </section>

              <section className="mt-8">
                <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                  Hébergement
                </h3>
                <p className="text-[15px] leading-relaxed text-[#334155]">
                  Ce site est hébergé sur l'infrastructure cloud Emergent
                  (Cloudflare + Kubernetes). Les données sont stockées en
                  Union européenne conformément au RGPD.
                </p>
              </section>

              <section className="mt-8">
                <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                  Propriété intellectuelle
                </h3>
                <p className="text-[15px] leading-relaxed text-[#334155]">
                  L'ensemble des contenus (textes, images, illustrations, logo,
                  code source) présents sur ce site sont la propriété
                  exclusive de HM Conseil Digital, sauf mentions contraires.
                  Toute reproduction, même partielle, est interdite sans
                  autorisation écrite préalable.
                </p>
              </section>

              <section className="mt-8">
                <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                  Données personnelles
                </h3>
                <p className="text-[15px] leading-relaxed text-[#334155]">
                  Les informations collectées via le formulaire de contact sont
                  destinées exclusivement à HM Conseil Digital pour traiter
                  votre demande de devis. Conformément au RGPD, vous disposez
                  d'un droit d'accès, de rectification et de suppression de
                  vos données personnelles en écrivant à{" "}
                  <a href={`mailto:${COMPANY.email}`} className="text-[#2D7A4D]">
                    {COMPANY.email}
                  </a>
                  .
                </p>
              </section>

              <section className="mt-8">
                <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                  Certifications professionnelles
                </h3>
                <ul className="space-y-1.5 text-[15px] text-[#334155]">
                  <li>• RGE QualiPV — installateur photovoltaïque certifié</li>
                  <li>• Qualibat 7141 — isolation thermique extérieure</li>
                  <li>• Attestation de capacité fluides frigorigènes</li>
                  <li>• Garantie décennale — AXA Assurances Professionnelles</li>
                  <li>• Mandataire administratif et financier MaPrimeRénov'</li>
                </ul>
              </section>

              <div className="mt-12 pt-6 border-t border-[#e2d9ce] text-xs text-[#64748B]">
                Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}.
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
